import { Box, Button, Tooltip, Typography } from '@mui/material';
import { insuranceActions, insuranceSelector } from '../../../model';
import { useDispatch, useSelector } from 'react-redux';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DEACTIVATE_DOC, Q_DOCS, UPDATE_DOCUMENTS_LENDER_ACCESS } from '../../../api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { UploadedFileRow } from './UploadedFileRow';
import { useStepperState } from 'modules/vendorOpportunity/insurance/hooks/useStepperState';
import { coverageStatusConfig } from 'modules/vendorOpportunity/insurance/constants';
import { CoverageStatusLabels } from 'modules/vendorOpportunity/insurance/types';
import { useMemo } from 'react';
import { UserProfile } from 'types';
import moment from 'moment';
import { DateFormats } from '../../../../../../constants/common';
import FileUploadModal from 'pages/VendorOpportunity/FileUploadModal';
import { documentsByKey } from 'pages/Prequal/constants';

export const CertificateUploaded = () => {
  const dispatch = useDispatch();

  const vo = useSelector((state: any) => state.vo);
  const account = useSelector((state: any) => state.account);
  const selectedSubmission = useSelector(insuranceSelector.selectedSubmission)!;
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate)!;

  const { showNotification } = useNotifications();
  const { stepperUnCompleteVerifyCoverage } = useStepperState();

  const [getFileAfterReplace, { loading: isDocuentsLoading }] = useLazyQuery(Q_DOCS, {
    onCompleted(docResponse) {
      dispatch(insuranceActions.setInsuranceCertificate(docResponse.documents[0]));
      showNotification('Insurance Certificate Uploaded Successfully!');
    },
  });

  const [deleteFile, { loading: replaceFileLoading }] = useMutation(DEACTIVATE_DOC);
  const [updateLenderDocumentsAccess, { loading: sendToLenderLoading }] = useMutation(UPDATE_DOCUMENTS_LENDER_ACCESS);

  const { isCertificateSharedWithLender, notifiedUserProfiles } = useMemo(() => {
    const documentCreditSubmission = insuranceCertificate.documentCreditSubmission?.find(
      dcs => dcs?.creditSubmissionId === selectedSubmission?.creditSubmissionId
    );
    return {
      isCertificateSharedWithLender: !!documentCreditSubmission?.lenderAccess,
      notifiedUserProfiles: (insuranceCertificate?.notifiedUserProfiles || []) as UserProfile[],
    };
  }, [selectedSubmission, insuranceCertificate]);

  const handleDeleteFile = async () => {
    try {
      await deleteFile({
        variables: {
          id: insuranceCertificate.documentId,
        },
      });
      dispatch(insuranceActions.setInsuranceCertificate(null));
      stepperUnCompleteVerifyCoverage();
      dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified]));
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  const handleReplaceFile = async ({ document, success }: { document: string; success: boolean }) => {
    try {
      if (document && success) {
        await deleteFile({
          variables: {
            id: insuranceCertificate.documentId,
          },
        });
        await getFileAfterReplace({ variables: { docId: document } });
      }
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  const sendToLender = async () => {
    try {
      await updateLenderDocumentsAccess({
        variables: {
          batchUpdateLenderDocumentAccessInput: {
            creditSubmissionId: selectedSubmission.creditSubId,
            documents: [
              {
                documentId: insuranceCertificate.documentId,
                lenderAccess: true,
                dealerAccess: true,
              },
            ],
          },
        },
      });
      dispatch(
        insuranceActions.setInsuranceCertificate({
          ...insuranceCertificate,
          creditSubmissionId: selectedSubmission.creditSubId,
        })
      );
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  return (
    <>
      <Box mb={2}>
        {isCertificateSharedWithLender ? (
          <Box display="flex">
            <CheckCircleOutlineIcon color="primary" />
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} ml={0.5}>
                <Typography component="p" variant="body1">
                  You have sent the Certificate of Insurance to {selectedSubmission.lenderName}
                </Typography>
                <Typography variant="body2">
                  {moment(insuranceCertificate.createdDateTime).format(DateFormats.TimeWithShortDate)}
                </Typography>
              </Box>
              {notifiedUserProfiles.length > 0 && (
                <Box mb={1} ml={0.5}>
                  <Typography component="span" variant="subtitle2" sx={{ color: '#00000099' }}>
                    Additional Notification Contacts:{' '}
                  </Typography>
                  <Typography component="span" variant="body2">
                    {notifiedUserProfiles.map(u => `${u.fullName}${u.title ? `, ${u.title}` : ''}`).join(' • ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Tooltip title="First, choose a submission" placement="top" disableHoverListener={!!selectedSubmission}>
            <span>
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={sendToLender}
                loading={sendToLenderLoading}
                sx={{ marginRight: 1 }}
                disabled={!selectedSubmission}
              >
                Send COI to Lender
              </Button>
            </span>
          </Tooltip>
        )}
        <FileUploadModal
          vo={vo}
          vp={account.vendorProfile}
          equipmentUpload={false}
          buttonDescription={() => 'Replace file'}
          creditSubmissionId={selectedSubmission?.creditSubId}
          predefinedFileType={documentsByKey.insurance_certificate.value}
          relatedTo="submission"
          onFileUploaded={handleReplaceFile}
        />
      </Box>
      <UploadedFileRow onRemove={handleDeleteFile} />
    </>
  );
};
